import { cn } from "@ui/lib/utils";
import { useTranslations } from "next-intl";
import { DottedBorder } from "./GradientBorder";
import clsx from "clsx";
import { getImageProps } from "@utilities/payload";
import type { CustomTableBlock, Media } from "@mw/shared/payload-types";
import Image from "next/image";

// Derive from payload types to stay in sync with CMS
type ColumnConfig = NonNullable<CustomTableBlock["columnConfig"]>[number];
export type ColumnColor = NonNullable<ColumnConfig["textColor"]>;

const COLUMN_COLOR_CLASSES: Record<ColumnColor, string> = {
  default: "",
  gray: "tw-text-gray-500",
};

export interface TableSimpleColumn {
  key: string;
  label?: string;
  color?: ColumnColor;
  headerIcon?: Media;
}

export interface TableSimpleCell {
  value: React.ReactNode | string;
  icon?: React.ReactNode;
  color?: ColumnColor;
}

export interface TableSimpleProps {
  columns: TableSimpleColumn[];
  rows: Record<string, TableSimpleCell | null | undefined>[];
  withHeaderHighlighted?: boolean;
  endingBorder?: boolean;
  className?: string;
}

export function TableSimple({
  columns,
  rows,
  withHeaderHighlighted = true,
  endingBorder = true,
  className,
}: TableSimpleProps) {
  const t = useTranslations("ClientKeys.Components.TableSimple");
  const hasHeaderRow = columns.some(column => !!column.label?.trim().length || !!column.headerIcon);

  if (!columns.length || !rows.length) {
    return null;
  }

  const gridTemplateColumns = Array(columns.length).fill("minmax(140px, 1fr)").join(" ");

  return (
    <div className={cn("tw-w-full tw-overflow-x-auto", className)}>
      <div aria-label={t("aria_label")} className='tw-grid tw-min-w-fit' style={{ gridTemplateColumns }} role='table'>
        {/* Header row */}
        {hasHeaderRow && (
          <div className='contents' role='rowgroup'>
            {columns.map((column, colIndex) => {
              const iconProps = getImageProps(column.headerIcon, { placeholder: true });
              return (
                <div
                  key={`header-${colIndex}`}
                  className={clsx(
                    "tw-flex tw-flex-col tw-gap-2 tw-px-2.5 tw-py-5 tw-text-left tw-type-sm md:tw-type-base",
                    withHeaderHighlighted && "tw-opacity-60",
                  )}
                  role='columnheader'
                >
                  {iconProps && <Image {...iconProps} className='tw-h-5 tw-max-w-48' alt='' />}
                  {column.label && column.label}
                </div>
              );
            })}
          </div>
        )}

        {/* Data rows */}
        {rows.map((row, rowIndex) => (
          <div key={`row-${rowIndex}`} className='tw-relative tw-contents' role='row'>
            {columns.map((column, colIndex) => {
              const cell = row[column.key];
              const hasIcon = cell?.icon != null;
              const shouldRenderTopBorder = rowIndex > 0 || hasHeaderRow;
              const cellColor = cell?.color ?? column.color ?? "default";

              return (
                <div
                  key={`cell-${rowIndex}-${colIndex}`}
                  className={clsx(
                    "tw-relative tw-break-words tw-whitespace-pre-wrap tw-px-2.5 tw-py-8 tw-text-left tw-type-sm md:tw-type-base",
                    COLUMN_COLOR_CLASSES[cellColor],
                    hasIcon && "tw-pl-0",
                  )}
                  role='cell'
                >
                  {shouldRenderTopBorder && <DottedBorder side='top' />}
                  {rowIndex === rows.length - 1 && endingBorder && <DottedBorder side='bottom' />}
                  {hasIcon ? (
                    <div className='tw-flex tw-items-start tw-gap-4'>
                      <div className='tw-shrink-0'>{cell.icon}</div>
                      <div className='tw-flex-1'>{cell?.value ?? ""}</div>
                    </div>
                  ) : (
                    (cell?.value ?? "")
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

TableSimple.displayName = "TableSimple";
